import LinkIcon from "@mui/icons-material/Link";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import * as jwt from "jsonwebtoken";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { Identity, useIdentity } from "../../containers/IdentityContainer";
import { getRedirectUrl } from "../../utils/redirectManager";
import { createUnauthenticatedPostgrest } from "../../utils/unauthenticatedApi";
// import logo from "../../../airplx-logo-transparent-square-no-text.png";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const MagicLinkLoginButton: React.FC<{}> = () => {
  const { t } = useTranslation();

  return (
    <Tooltip title={t('login.messages.magicLinkTooltip')}>
      <Button
        component={Link}
        to="/login/link"
        variant="outlined"
        fullWidth
        startIcon={<LinkIcon />}
        sx={{
          borderRadius: 1,
          borderColor: "divider",
          "&:hover": {
            borderColor: "text.primary",
            backgroundColor: "action.hover",
          },
        }}
      >
        {t('login.buttons.loginMagicLink')}
      </Button>
    </Tooltip>
  );
};

type LoginStep = "email" | "password" | "magic-link" | "setup-password";

export const Login: React.FC<{
  magicLink?: boolean;
}> = () => {
  const { t } = useTranslation();
  const {
    isLoggedIn,
    loginWithMicrosoft,
    loginWithGoogle,
    loginWithPassword,
    hasPassword,
  } = useIdentity();
  // Use unauthenticated client for whitelist check (user not logged in yet)
  const postgrest = React.useMemo(() => createUnauthenticatedPostgrest(), []);

  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [step, setStep] = React.useState<LoginStep>("email");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [showEmailLogin, setShowEmailLogin] = React.useState<boolean>(false);

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  const handleEmailSubmit = async () => {
    if (!email) {
      setError(t('login.errors.enterEmail'));
      return;
    }

    setLoading(true);
    setError("");

    try {
      // First check if whitelisted for password auth
      const isWhitelisted = await postgrest.rpc(
        "is_password_auth_whitelisted",
        {
          p_email: email,
        }
      );

      if (!isWhitelisted.data) {
        // Not whitelisted - show helpful error
        setError(t('login.errors.notWhitelisted'));
        setLoading(false);
        return;
      }

      // Whitelisted - check if they have a password set
      const emailHasPassword = await hasPassword(email);

      if (emailHasPassword) {
        setStep("password");
      } else {
        // Whitelisted but no password set - prompt to set up password
        setStep("setup-password");
      }
    } catch (err) {
      setError(t('login.errors.genericError'));
      console.error("Error checking email:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    if (!password) {
      setError(t('login.errors.enterPassword'));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const success = await loginWithPassword(email, password);

      if (success) {
        // Redirect to stored URL or default to home page
        // Use window.location to force full page reload so identity re-initializes
        const redirectUrl = getRedirectUrl("/");
        window.location.href = `/#${redirectUrl}`;
      } else {
        setError(t('login.errors.invalidCredentials'));
      }
    } catch (err) {
      setError(t('login.errors.genericError'));
      console.error("Error logging in:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setPassword("");
    setError("");
    setShowEmailLogin(false);
  };

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        sx={{ height: "100%", width: "100%" }}
      >
        <Box
          sx={{
            width: 368,
            margin: "auto",
            border: 2,
            borderColor: "divider",
            p: 3,
            mt: 20,
          }}
        >
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            <Typography align="center" variant="h4">
              {t('login.title')}
            </Typography>
            <Typography
              align="center"
              variant="subtitle1"
              color="text.secondary"
            >
              {step === "email" && t('login.subtitle.email')}
              {step === "password" && t('login.subtitle.password')}
              {step === "magic-link" && t('login.subtitle.magicLink')}
              {step === "setup-password" && t('login.subtitle.setupPassword')}
            </Typography>
          </Stack>
          <br />
          <br />

          {/* Primary: SSO Options */}
          <Stack direction="column" spacing={2}>
            {step === "email" && (
              <>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ borderRadius: 1, borderColor: "divider" }}
                  onClick={async () => {
                    await loginWithMicrosoft();
                    // Redirect to stored URL or default to home page
                    // Use window.location to force full page reload so identity re-initializes
                    const redirectUrl = getRedirectUrl("/");
                    window.location.href = `/#${redirectUrl}`;
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: "100%" }}
                  >
                    <img
                      src="https://img.icons8.com/color/48/000000/microsoft.png"
                      style={{ width: 25 }}
                    />
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      {t('login.buttons.loginMicrosoft')}
                    </Typography>
                    <span></span>
                  </Stack>
                </Button>

                <GoogleLogin
                  width="320px"
                  onSuccess={async (credentialResponse) => {
                    const token = credentialResponse.credential;
                    const identity = jwt.decode(token);
                    await loginWithGoogle(token, identity as Identity);
                    // Redirect to stored URL or default to home page
                    // Use window.location to force full page reload so identity re-initializes
                    const redirectUrl = getRedirectUrl("/");
                    window.location.href = `/#${redirectUrl}`;
                  }}
                  onError={() => {
                    console.log("Login Failed");
                  }}
                />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    mt: 2,
                    cursor: "pointer",
                    color: "text.secondary",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => setShowEmailLogin(true)}
                >
                  {t('login.buttons.usePassword')}
                </Typography>

                {showEmailLogin && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: "block" }}
                    >
                      {t('login.messages.passwordRestricted')}
                    </Typography>
                    <TextField
                      label={t('login.fields.email')}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleEmailSubmit()
                      }
                      fullWidth
                      error={Boolean(error)}
                      helperText={error}
                      disabled={loading}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleEmailSubmit}
                      fullWidth
                      disabled={loading || !email}
                      size="small"
                      sx={{ borderRadius: 1, borderColor: "divider" }}
                    >
                      {loading ? <CircularProgress size={20} /> : t('common.continue')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowEmailLogin(false);
                        setEmail("");
                        setError("");
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </>
                )}
              </>
            )}

            {step === "password" && (
              <>
                <TextField
                  label={t('login.fields.email')}
                  type="email"
                  value={email}
                  fullWidth
                  disabled
                />
                <TextField
                  label={t('login.fields.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handlePasswordLogin()}
                  fullWidth
                  autoFocus
                  error={Boolean(error)}
                  helperText={error}
                  disabled={loading}
                />
                <Button
                  variant="contained"
                  onClick={handlePasswordLogin}
                  fullWidth
                  disabled={loading || !password}
                  sx={{ borderRadius: 1 }}
                >
                  {loading ? <CircularProgress size={24} /> : t('login.buttons.login')}
                </Button>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Button size="small" onClick={handleBackToEmail}>
                    {t('login.buttons.changeEmail')}
                  </Button>
                  <Button
                    size="small"
                    component={Link}
                    to="/forgot-password"
                    state={{ email }}
                  >
                    {t('login.buttons.forgotPassword')}
                  </Button>
                </Stack>
              </>
            )}

            {step === "setup-password" && (
              <>
                <TextField
                  label={t('login.fields.email')}
                  type="email"
                  value={email}
                  fullWidth
                  disabled
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  {t('login.messages.setupRequired')}
                </Typography>
                <Button
                  variant="contained"
                  component={Link}
                  to="/signup"
                  state={{ email }}
                  fullWidth
                  sx={{ borderRadius: 1 }}
                >
                  {t('login.buttons.setupPassword')}
                </Button>
                <Button size="small" onClick={handleBackToEmail} fullWidth>
                  {t('login.buttons.changeEmail')}
                </Button>
              </>
            )}

            {step === "magic-link" && (
              <>
                <TextField
                  label={t('login.fields.email')}
                  type="email"
                  value={email}
                  fullWidth
                  disabled
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  {t('login.messages.noPasswordSet')}
                </Typography>
                <Button
                  variant="contained"
                  component={Link}
                  to="/login/link"
                  state={{ email }}
                  fullWidth
                  sx={{ borderRadius: 1 }}
                >
                  {t('login.buttons.sendLoginLink')}
                </Button>
                <Button size="small" onClick={handleBackToEmail} fullWidth>
                  {t('login.buttons.changeEmail')}
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
